/**
 * JS-1082: derive ECharts price-chart overlay specs from a resolved idea's
 * ALREADY-STAMPED facts -- strikes, breakeven, and the 21-DTE exit window.
 * No new pricing math anywhere in this module: every number plotted is read
 * straight off `entry.strikes` / `entry.breakevens` / `entry.expiry` /
 * `entry.exit_rule.dte_floor`, the SAME facts the drawer already displays
 * verbatim (see `MultiStrategyScreenTable.tsx`'s "Breakeven"/"Strikes" rows).
 *
 * Deliberately absent: a plottable "stop-ref" PRICE level. The stamped
 * exit_rule (`domains/watches/lib/exitRuleWatchTriggers.ts`'s
 * `mapExitRuleToWatchFields`) expresses a stop as a P&L dollar threshold or a
 * value-multiple of the structure's OWN premium -- neither is a point on the
 * UNDERLYING's price axis, and converting one into an underlying price would
 * require re-pricing the option (new math the ticket explicitly rules out,
 * and a claim this module has no authority to make -- ADR-011). The
 * honest choice is to surface the stop as the existing FACTUAL sentence
 * `screenerLabels.exitRuleCopy` already composes (e.g. "Stop: value falls to
 * 0.5x the debit paid"), in the context banner, rather than fabricate a
 * false price level on the chart.
 */

import type { MultiStrategyScreenEntry } from "@/domains/trade-ideas/lib/contracts";
import { parseDecimalString, projectedDisplayExpiry, projectedDisplayStrikes } from "@/domains/trade-ideas/lib/screenerLabels";

export interface PriceLevelMarkLine {
  /** A stable key for React lists / test assertions. */
  id: string;
  price: number;
  label: string;
  color: string;
  lineType: "dashed";
}

const STRIKE_LINE_COLOR = "#94a3b8";
const BREAKEVEN_LINE_COLOR = "#14b8a6"; // teal, per the wireframe

/**
 * Strike levels (dashed) -- read from `entry.strikes`, deduplicated (a
 * condor's two short/long strikes on the SAME side render as one line).
 * Malformed/unparseable entries are silently skipped (honest-absence, never
 * a fabricated price).
 *
 * JS-1094 (demo blocker FIX 4): projected via `projectedDisplayStrikes`, the
 * SAME priced-legs projection the screener/edition drawers already use
 * (screenerLabels.ts) -- a real, priced CREDIT entry (bull-put/bear-call/
 * condor) never populates canonical `strikes` at all (only the debit
 * pipeline does), so a raw `entry.strikes` read left every credit idea's
 * chart with no strike lines even though the SAME entry's own drawer already
 * shows strikes (via that projection) elsewhere on the page. Prefers
 * canonical `strikes` when present, so a debit entry's behavior is
 * unchanged.
 */
export function strikeMarkLines(
  entry: Pick<MultiStrategyScreenEntry, "strikes" | "priced_legs">,
): PriceLevelMarkLine[] {
  const seen = new Set<number>();
  const lines: PriceLevelMarkLine[] = [];
  for (const raw of projectedDisplayStrikes(entry)) {
    const price = parseDecimalString(raw);
    if (price === null || seen.has(price)) continue;
    seen.add(price);
    lines.push({ id: `strike-${raw}`, price, label: `Strike ${raw}`, color: STRIKE_LINE_COLOR, lineType: "dashed" });
  }
  return lines;
}

/**
 * Breakeven levels (teal dashed) -- read verbatim from `entry.breakevens`,
 * the SAME field the drawer's "Breakeven" row already displays. Never
 * recomputed here.
 */
export function breakevenMarkLines(entry: Pick<MultiStrategyScreenEntry, "breakevens">): PriceLevelMarkLine[] {
  const lines: PriceLevelMarkLine[] = [];
  for (const raw of entry.breakevens ?? []) {
    const price = parseDecimalString(raw);
    if (price === null) continue;
    lines.push({ id: `breakeven-${raw}`, price, label: `Breakeven ${raw}`, color: BREAKEVEN_LINE_COLOR, lineType: "dashed" });
  }
  return lines;
}

/** Combine strike + breakeven lines into the ECharts `markLine.data` shape. */
export function priceLevelsToEchartsMarkLineData(lines: PriceLevelMarkLine[]) {
  return lines.map((line) => ({
    yAxis: line.price,
    name: line.label,
    lineStyle: { color: line.color, type: line.lineType },
    label: { show: false },
  }));
}

export interface ExitWindowShading {
  /** ISO date the 21-DTE (or whatever dte_floor is) exit window begins. */
  startDate: string;
  /** ISO date the structure's own expiry -- the end of the shaded band. */
  endDate: string;
  dteFloor: number;
}

/**
 * The exit window's start date: `entry.expiry` minus `dte_floor` calendar
 * days -- plain date arithmetic on two ALREADY-STAMPED facts (never a
 * repricing, never a trading-calendar simulation). Returns `null`
 * (honest-absence) when either fact is missing or `expiry` doesn't parse --
 * this is the ticket's own "if cheap" allowance: skip the shading rather
 * than approximate a trading-calendar it doesn't have.
 *
 * JS-1094 (demo blocker FIX 4): `expiry` is projected via
 * `projectedDisplayExpiry` (the SAME priced-legs projection the screener/
 * edition drawers use), not read canonically -- a real, priced CREDIT entry
 * never populates canonical `expiry` (see `strikeMarkLines`'s identical
 * fix above for the full "why"). Prefers canonical `expiry` when present.
 */
export function exitWindowShading(
  entry: Pick<MultiStrategyScreenEntry, "expiry" | "exit_rule" | "strikes" | "priced_legs">,
): ExitWindowShading | null {
  const expiry = projectedDisplayExpiry(entry);
  const dteFloor = entry.exit_rule?.dte_floor;
  if (!expiry || typeof dteFloor !== "number" || !Number.isFinite(dteFloor) || dteFloor < 0) {
    return null;
  }
  const expiryDate = new Date(`${expiry}T00:00:00Z`);
  if (Number.isNaN(expiryDate.getTime())) return null;
  const startDate = new Date(expiryDate.getTime() - dteFloor * 24 * 60 * 60 * 1000);
  return {
    startDate: startDate.toISOString().slice(0, 10),
    endDate: expiry,
    dteFloor,
  };
}

/** ECharts `markArea.data` shape shading the exit window on the x (category/date) axis. */
export function exitWindowToEchartsMarkAreaData(shading: ExitWindowShading | null) {
  if (!shading) return [];
  return [
    [
      { xAxis: shading.startDate, itemStyle: { color: "rgba(246, 70, 93, 0.08)" } },
      { xAxis: shading.endDate },
    ],
  ];
}
